'use client';

import { c as _c } from "react/compiler-runtime";
import 'dayjs/locale/ko';
import { Avatar } from '@common/components';
import SvgBookmarkFill from '@common/icon/SvgBookmarkFill';
import SvgChatbubbleTypingFill from '@common/icon/SvgChatbubbleTypingFill';
import SvgChevronright from '@common/icon/SvgChevronright';
import SvgSend from '@common/icon/SvgSend';
import { cn } from '@core/utils';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import useEmblaCarousel from 'embla-carousel-react';
import { type ComponentType, memo, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { AuthSdk, UnauthSdk } from '@/api';
import { BOOKMARK_TYPE } from '@/api';
import { CustomLink } from '@/app/_components/CustomLink';
import { openShareDrawer } from '@/app/_components/GlobalModal/_components/ShareDrawer';
import { Image } from '@/components/client';
import { FestivalStatusBadge } from '@/components/molecule/FestivalStatusBadge';
import { DONATION_ROUTES, ENV, ROUTES, SELECTOR, WEL_MARKET_ROUTES } from '@/constants';
import { useBookmark } from '@/hooks/useBookmark';
import { useCustomRouter } from '@/hooks/useCustomRouter';
import { useHaptic } from '@/hooks/useHaptic';
import { buildDetailSlug } from '@/utils/detailSlug';
import { formatDateRange } from '@/utils/formatDateRange';
type UnauthFeedResponse = NonNullable<Awaited<ReturnType<typeof UnauthSdk.findFeeds>>['data']>;
type AuthFeedResponse = NonNullable<Awaited<ReturnType<typeof AuthSdk.getFeedList>>['data']>;
type UnauthFeedItem = NonNullable<NonNullable<UnauthFeedResponse['context']>['contents']>[number];
type AuthFeedItem = NonNullable<NonNullable<AuthFeedResponse['context']>['contents']>[number];
export type FeedItem = UnauthFeedItem | AuthFeedItem;
dayjs.extend(relativeTime);
const formatDate = (value?: string) => {
  if (!value || !dayjs(value).isValid()) return '';
  const date = dayjs(value);
  return dayjs().diff(date, 'day') < 7 ? date.locale('ko').fromNow() : date.format('YYYY.MM.DD');
};
const normalizeRate = (rate?: number) => {
  if (typeof rate !== 'number' || Number.isNaN(rate)) return 0;
  return Math.round(rate <= 1 ? rate * 100 : rate);
};
const formatValue = (value?: number, unit?: string) => typeof value === 'number' ? `${value.toLocaleString('ko-KR')}${unit ?? ''}` : '-';
type FeedRouteEntry = {
  key: 'contentIdx';
  resolve: (idx: string, item: FeedItem) => string;
} | {
  key: 'contentId';
  resolve: (id: number, item: FeedItem) => string;
};
const FEED_DETAIL_ROUTE_MAP: Partial<Record<NonNullable<FeedItem['feedContentType']>, FeedRouteEntry>> = {
  POLICY: {
    key: 'contentIdx',
    resolve: (idx, item) => ROUTES.POLICY_DETAIL.pathname({
      idIdx: item.slugId ?? buildDetailSlug(idx, item.title)
    })
  },
  BEST_CONTENTS: {
    key: 'contentIdx',
    resolve: (idx, item) => ROUTES.POLICY_INFO_DETAIL.pathname({
      idIdx: item.slugId ?? buildDetailSlug(idx, item.title)
    })
  },
  POLICY_CARD: {
    key: 'contentIdx',
    resolve: (idx, item) => ROUTES.POLICY_DETAIL.pathname({
      idIdx: item.slugId ?? buildDetailSlug(idx, item.title)
    })
  },
  HOMETOWN_NEWS: {
    key: 'contentIdx',
    resolve: (idx, item) => ROUTES.HOMETOWN_NEWS_DETAIL.pathname({
      idIdx: item.slugId ?? buildDetailSlug(idx, item.title)
    })
  },
  DONATION: {
    key: 'contentId',
    resolve: (id, item) => DONATION_ROUTES.HOMETOWN_BUSINESS_DETAIL.pathname({
      id: item.slugId ?? id
    })
  },
  HOME_PRODUCT: {
    key: 'contentId',
    resolve: (id, item) => DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
      id: item.slugId ?? id
    })
  }
};
const getFeedDetailHref = (item: FeedItem) => {
  if (item.feedContentType === 'LOCAL_MARKET') {
    const firstProduct = item.productList?.[0];
    return firstProduct?.productId ? WEL_MARKET_ROUTES.PRODUCT_DETAIL.pathname({
      productNumber: firstProduct.slugId ?? firstProduct.productId
    }) : ROUTES.BENEFIT.withSearchParams({
      searchParams: {
        tab: 'use'
      }
    });
  }
  if (item.feedContentType === 'THEME') {
    return ROUTES.FESTIVAL_LIST.pathname;
  }
  const entry = item.feedContentType ? FEED_DETAIL_ROUTE_MAP[item.feedContentType] : undefined;
  if (!entry) return;
  const value = item[entry.key];
  return value !== undefined ? (entry.resolve as (v: never, item: FeedItem) => string)(value as never, item) : undefined;
};
const FEED_BOOKMARK_TYPE_MAP: Partial<Record<NonNullable<FeedItem['feedContentType']>, BOOKMARK_TYPE>> = {
  POLICY: BOOKMARK_TYPE.POLICY,
  BEST_CONTENTS: BOOKMARK_TYPE.POLICY_INFO,
  POLICY_CARD: BOOKMARK_TYPE.POLICY,
  HOMETOWN_NEWS: BOOKMARK_TYPE.HOMETOWN_NEWS
};
function FeedCardHeader(t0) {
  const $ = _c(12);
  const {
    item
  } = t0;
  const isPolicy = item.feedContentType === "POLICY" || item.feedContentType === "CUSTOM_POLICY";
  let t1;
  if ($[0] !== item.publisherLogoUrl || $[1] !== item.publisherName) {
    t1 = <Avatar size={40} src={item.publisherLogoUrl} alt={item.publisherName} />;
    $[0] = item.publisherLogoUrl;
    $[1] = item.publisherName;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== item.publisherName) {
    t2 = item.publisherName ? <span className="flex-1 truncate font-600 text-Gray-900 typo-caption1">{item.publisherName}</span> : null;
    $[3] = item.publisherName;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== isPolicy || $[6] !== item.daysAgo) {
    t3 = isPolicy ? null : <span className="font-400 text-Gray-550 typo-caption1">{item.daysAgo}</span>;
    $[5] = isPolicy;
    $[6] = item.daysAgo;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t1 || $[9] !== t2 || $[10] !== t3) {
    t4 = <div className="flex items-center gap-12">{t1}{t2}{t3}</div>;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}
function calcTruncation(element: HTMLParagraphElement, text: string) {
  const lineHeight = parseFloat(getComputedStyle(element).lineHeight);
  const maxHeight = lineHeight * 2 + 1;
  const clone = element.cloneNode() as HTMLParagraphElement;
  Object.assign(clone.style, {
    position: 'fixed',
    visibility: 'hidden',
    top: '-9999px',
    width: `${element.clientWidth}px`
  });
  document.body.appendChild(clone);
  clone.textContent = text;
  if (clone.scrollHeight <= maxHeight) {
    clone.remove();
    return null;
  }
  const suffix = '... 더보기';
  let lo = 0;
  let hi = text.length;
  while (lo < hi) {
    const mid = Math.ceil((lo + hi) / 2);
    clone.textContent = text.slice(0, mid).trimEnd() + suffix;
    if (clone.scrollHeight <= maxHeight) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  clone.remove();
  return text.slice(0, lo).trimEnd();
}
function FeedCardBody(t0) {
  const $ = _c(18);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item.title) {
    t1 = item.title?.trim() || undefined;
    $[0] = item.title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const title = t1;
  let t2;
  if ($[2] !== item.contentPreview) {
    t2 = item.contentPreview?.trim() || undefined;
    $[2] = item.contentPreview;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const contentPreview = t2;
  const textRef = useRef(null);
  const [truncated, setTruncated] = useState(null);
  let t3;
  let t4;
  if ($[4] !== contentPreview) {
    t3 = () => {
      const element = textRef.current;
      if (!element || !contentPreview) {
        setTruncated(null);
        return;
      }
      setTruncated(calcTruncation(element, contentPreview));
    };
    t4 = [contentPreview];
    $[4] = contentPreview;
    $[5] = t3;
    $[6] = t4;
  } else {
    t3 = $[5];
    t4 = $[6];
  }
  useLayoutEffect(t3, t4);
  let t5;
  let t6;
  if ($[7] !== contentPreview) {
    t5 = () => {
      const element_0 = textRef.current;
      if (!element_0 || !contentPreview) {
        return;
      }
      let prevWidth = element_0.clientWidth;
      const ro = new ResizeObserver(() => {
        if (element_0.clientWidth === prevWidth) {
          return;
        }
        prevWidth = element_0.clientWidth;
        setTruncated(calcTruncation(element_0, contentPreview));
      });
      ro.observe(element_0);
      return () => ro.disconnect();
    };
    t6 = [contentPreview];
    $[7] = contentPreview;
    $[8] = t5;
    $[9] = t6;
  } else {
    t5 = $[8];
    t6 = $[9];
  }
  useEffect(t5, t6);
  if (!title && !contentPreview) {
    return null;
  }
  let t7;
  if ($[10] !== title) {
    t7 = title && <h3 className="font-600 text-Gray-black typo-body2">{title}</h3>;
    $[10] = title;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== contentPreview || $[13] !== truncated) {
    t8 = contentPreview && <p ref={textRef} className="font-400 text-Gray-800 typo-body4">{truncated ?? contentPreview}{truncated !== null ? "... " : " "}<span className="text-Gray-500 transition-colors duration-150 group-hover:text-Gray-700">더보기</span></p>;
    $[12] = contentPreview;
    $[13] = truncated;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t7 || $[16] !== t8) {
    t9 = <div className="flex flex-col gap-4">{t7}{t8}</div>;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  return t9;
}
function ImageContent(t0) {
  const $ = _c(12);
  const {
    item,
    priority,
    href
  } = t0;
  let t1;
  if ($[0] !== item.attachmentList) {
    t1 = item.attachmentList ?? [];
    $[0] = item.attachmentList;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = t1.flatMap(_temp);
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const images = t2;
  if (!images.length) {
    return null;
  }
  if (images.length === 1) {
    const t3 = priority ? "high" : undefined;
    let t4;
    if ($[4] !== images[0] || $[5] !== priority || $[6] !== t3) {
      t4 = <div className="relative aspect-square w-full overflow-hidden rounded-xsmall bg-Gray-200"><Image src={images[0]} alt={"\uD53C\uB4DC \uC774\uBBF8\uC9C0 1"} fill={true} sizes="(max-width: 600px) 100vw, 600px" preload={priority} fetchPriority={t3} className="object-cover" /></div>;
      $[4] = images[0];
      $[5] = priority;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  let t3;
  if ($[8] !== href || $[9] !== images || $[10] !== priority) {
    t3 = <ImageCarousel href={href} images={images} priority={priority} />;
    $[8] = href;
    $[9] = images;
    $[10] = priority;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
function _temp(a) {
  return a.imageUrl ? [a.imageUrl] : [];
}
function ImageCarousel(t0) {
  const $ = _c(29);
  const {
    images,
    priority,
    href
  } = t0;
  const router = useCustomRouter();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "start",
      containScroll: "trimSnaps"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [emblaRef, emblaApi] = useEmblaCarousel(t1);
  const [activeIndex, setActiveIndex] = useState(0);
  let t2;
  let t3;
  if ($[1] !== emblaApi) {
    t2 = () => {
      if (!emblaApi) {
        return;
      }
      const onSelect = () => setActiveIndex(emblaApi.selectedScrollSnap());
      onSelect();
      emblaApi.on("select", onSelect);
      return () => {
        emblaApi.off("select", onSelect);
      };
    };
    t3 = [emblaApi];
    $[1] = emblaApi;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] !== href || $[5] !== images || $[6] !== priority || $[7] !== router) {
    let t5;
    if ($[9] !== href || $[10] !== images.length || $[11] !== priority || $[12] !== router) {
      t5 = (url, index) => <div key={`${url}-${index}`} className={cn("relative aspect-square w-[calc((100%-8px)/1.5)] shrink-0 overflow-hidden rounded-xsmall bg-Gray-200", index < images.length - 1 && "mr-8", href && "cursor-pointer")} onClick={href ? () => router.push(href) : undefined}><Image src={url} alt={`피드 이미지 ${index + 1}`} fill={true} sizes="(max-width: 600px) 67vw, 400px" preload={priority && index === 0} fetchPriority={priority && index === 0 ? "high" : undefined} loading={priority && index === 1 ? "eager" : undefined} className="object-cover" /></div>;
      $[9] = href;
      $[10] = images.length;
      $[11] = priority;
      $[12] = router;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    t4 = images.map(t5);
    $[4] = href;
    $[5] = images;
    $[6] = priority;
    $[7] = router;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[14] !== t4) {
    t5 = <div className="flex">{t4}</div>;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== emblaRef || $[17] !== t5) {
    t6 = <div ref={emblaRef} className="cursor-grab overflow-hidden active:cursor-grabbing">{t5}</div>;
    $[16] = emblaRef;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== activeIndex || $[20] !== images) {
    let t8;
    if ($[22] !== activeIndex) {
      t8 = (_, index_0) => <div key={index_0} className={cn("size-6 rounded-pill transition-colors duration-200", {
        "bg-Gray-900": index_0 === activeIndex,
        "bg-Gray-300": index_0 !== activeIndex
      })} />;
      $[22] = activeIndex;
      $[23] = t8;
    } else {
      t8 = $[23];
    }
    t7 = images.map(t8);
    $[19] = activeIndex;
    $[20] = images;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[24] !== t7) {
    t8 = <div className="flex justify-center gap-4">{t7}</div>;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t6 || $[27] !== t8) {
    t9 = <div className="flex flex-col gap-8">{t6}{t8}</div>;
    $[26] = t6;
    $[27] = t8;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  return t9;
}
function ProgressContent(t0) {
  const $ = _c(34);
  const {
    item,
    priority
  } = t0;
  const {
    progress
  } = item;
  if (!progress) {
    return null;
  }
  let t1;
  if ($[0] !== progress.rate) {
    t1 = normalizeRate(progress.rate);
    $[0] = progress.rate;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const rate = t1;
  let t2;
  if ($[2] !== item.title || $[3] !== priority || $[4] !== progress.imageUrl) {
    t2 = progress.imageUrl && <div className="relative aspect-[16/10] w-full overflow-hidden rounded-xsmall bg-Gray-200"><Image src={progress.imageUrl} alt={item.title ?? ""} fill={true} sizes="(max-width: 600px) 100vw, 600px" preload={priority} fetchPriority={priority ? "high" : undefined} className="object-cover" /></div>;
    $[2] = item.title;
    $[3] = priority;
    $[4] = progress.imageUrl;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const t3 = progress.label ?? "\uC9C4\uD589\uB960";
  let t4;
  if ($[6] !== rate || $[7] !== t3) {
    t4 = <span>{t3} {rate}%</span>;
    $[6] = rate;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== progress.currentValue || $[10] !== progress.valueUnit) {
    t5 = formatValue(progress.currentValue, progress.valueUnit);
    $[9] = progress.currentValue;
    $[10] = progress.valueUnit;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== progress.targetValue || $[13] !== progress.valueUnit) {
    t6 = formatValue(progress.targetValue, progress.valueUnit);
    $[12] = progress.targetValue;
    $[13] = progress.valueUnit;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = <span>{t5} /{" "}{t6}</span>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t4 || $[19] !== t7) {
    t8 = <div className="flex items-center justify-between font-600 text-Gray-600 typo-caption1">{t4}{t7}</div>;
    $[18] = t4;
    $[19] = t7;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  const t9 = `${Math.min(rate, 100)}%`;
  let t10;
  if ($[21] !== t9) {
    t10 = <div className="h-4 flex-1 overflow-hidden rounded-pill bg-Gray-50"><div className="h-full rounded-pill bg-O-500 transition-[width] duration-500 ease-out" style={{
        width: t9
      }} /></div>;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  let t11;
  if ($[23] !== rate) {
    t11 = <span className="font-600 text-O-500 typo-body4">{rate}%</span>;
    $[23] = rate;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  let t12;
  if ($[25] !== t10 || $[26] !== t11) {
    t12 = <div className="flex items-center gap-16">{t10}{t11}</div>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] !== t12 || $[29] !== t8) {
    t13 = <div className="flex flex-col gap-4">{t8}{t12}</div>;
    $[28] = t12;
    $[29] = t8;
    $[30] = t13;
  } else {
    t13 = $[30];
  }
  let t14;
  if ($[31] !== t13 || $[32] !== t2) {
    t14 = <div className="flex flex-col gap-8">{t2}{t13}</div>;
    $[31] = t13;
    $[32] = t2;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  return t14;
}
function ProductContent(t0) {
  const $ = _c(17);
  const {
    item,
    priority
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "start",
      containScroll: "trimSnaps",
      dragFree: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [emblaRef] = useEmblaCarousel(t1);
  if (!item.productList?.length) {
    return null;
  }
  let t2;
  if ($[1] !== item.contentId || $[2] !== item.feedContentType || $[3] !== item.productList || $[4] !== item.slugId || $[5] !== priority) {
    let t3;
    if ($[7] !== item.contentId || $[8] !== item.feedContentType || $[9] !== item.slugId || $[10] !== priority) {
      t3 = (product, index) => {
        const card = <><div className="relative size-140 overflow-hidden rounded-xsmall bg-Gray-200"><Image src={product.productImageUrl} alt={product.productName ?? ""} fill={true} sizes="140px" preload={priority && index === 0} fetchPriority={priority && index === 0 ? "high" : undefined} loading={priority && index < 4 ? "eager" : undefined} className="object-cover" /></div>{product.region && <div className="mt-8 flex items-center gap-4"><span className="font-600 text-Gray-600 typo-caption1">{product.region}</span><SvgChevronright className="size-12 text-Gray-400" /></div>}<p className="mt-4 line-clamp-2 font-500 text-Gray-900 typo-body4">{product.productName}</p>{product.rating || product.reviewCount ? <div className="mt-8 flex items-center gap-4"><span className="font-400 text-Y-550 typo-caption2">{Array.from({
                length: 5
              }, (_, s) => s < Math.round(product.rating ?? 0) ? "\u2605" : "\u2606").join("")}</span><span className="font-400 text-Gray-600 typo-caption2">({product.reviewCount?.toLocaleString("ko-KR") ?? 0})</span></div> : null}</>;
        const key = product.productId ?? `${product.productName}-${index}`;
        const isLocalMarket = item.feedContentType === "LOCAL_MARKET";
        const href = product.productId ? isLocalMarket ? WEL_MARKET_ROUTES.PRODUCT_DETAIL.pathname({
          productNumber: product.slugId ?? product.productId
        }) : DONATION_ROUTES.GIFTS_DETAIL.pathname({
          productNumber: product.slugId ?? product.productId
        }) : item.contentId && !isLocalMarket ? DONATION_ROUTES.HOMETOWNS_DETAIL.pathname({
          id: item.slugId ?? item.contentId
        }) : undefined;
        return href ? <CustomLink key={key} prefetch={true} href={href} className="group w-140 shrink-0">{card}</CustomLink> : <div key={key} className="w-140 shrink-0">{card}</div>;
      };
      $[7] = item.contentId;
      $[8] = item.feedContentType;
      $[9] = item.slugId;
      $[10] = priority;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    t2 = item.productList.map(t3);
    $[1] = item.contentId;
    $[2] = item.feedContentType;
    $[3] = item.productList;
    $[4] = item.slugId;
    $[5] = priority;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[12] !== t2) {
    t3 = <div className="flex gap-8 px-16 xl:px-24">{t2}</div>;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== emblaRef || $[15] !== t3) {
    t4 = <div ref={emblaRef} className="-mx-16 cursor-grab overflow-hidden active:cursor-grabbing xl:-mx-24">{t3}</div>;
    $[14] = emblaRef;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  return t4;
}
const FESTIVAL_IMAGE_PLACEHOLDER = '/images/local-market/placeholder-thumbnail.png';
function TourContent(t0) {
  const $ = _c(11);
  const {
    item,
    priority
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      align: "start",
      containScroll: "trimSnaps",
      dragFree: true
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [emblaRef] = useEmblaCarousel(t1);
  if (!item.tourFestivalList?.length) {
    return null;
  }
  let t2;
  if ($[1] !== item.tourFestivalList || $[2] !== priority) {
    let t3;
    if ($[4] !== priority) {
      t3 = (festival, index) => {
        const card = <><div className="relative aspect-[4/3] w-full overflow-hidden rounded-xsmall bg-Gray-200"><Image src={festival.firstImage || FESTIVAL_IMAGE_PLACEHOLDER} alt={festival.title ?? ""} fill={true} sizes="180px" preload={priority && index === 0} fetchPriority={priority && index === 0 ? "high" : undefined} loading={priority && index < 4 ? "eager" : undefined} className="object-cover" unoptimized={true} />{festival.status && <FestivalStatusBadge status={festival.status} className="absolute left-8 top-8" />}</div><p className="mt-8 line-clamp-2 font-500 text-Gray-900 typo-body4">{festival.title}</p><div className="mt-4 flex flex-col gap-4 font-400 text-Gray-600 typo-caption1">{festival.eventstartdate && festival.eventenddate && <span>{formatDateRange(festival.eventstartdate, festival.eventenddate)}</span>}{festival.addr1 && <span className="truncate">{festival.addr1}</span>}</div></>;
        const key = festival.id ?? `${festival.title}-${index}`;
        return festival.id ? <CustomLink key={key} prefetch={true} href={ROUTES.FESTIVAL_DETAIL.pathname({
          idIdx: festival.slugId ?? buildDetailSlug(festival.id, festival.title)
        })} className="group w-180 shrink-0">{card}</CustomLink> : <div key={key} className="w-180 shrink-0">{card}</div>;
      };
      $[4] = priority;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    t2 = item.tourFestivalList.map(t3);
    $[1] = item.tourFestivalList;
    $[2] = priority;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[6] !== t2) {
    t3 = <div className="flex gap-8 px-16 xl:px-24">{t2}</div>;
    $[6] = t2;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== emblaRef || $[9] !== t3) {
    t4 = <div ref={emblaRef} className="-mx-16 cursor-grab overflow-hidden active:cursor-grabbing xl:-mx-24">{t3}</div>;
    $[8] = emblaRef;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  return t4;
}
function PolicyGroupContent(t0) {
  const $ = _c(12);
  const {
    item
  } = t0;
  if (!item.customPolicyList?.length) {
    return null;
  }
  let t1;
  if ($[0] !== item.title) {
    t1 = item.title?.trim();
    $[0] = item.title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const title = t1;
  let t2;
  if ($[2] !== title) {
    t2 = title && <h2 className="flex-1 truncate font-600 text-Gray-black typo-body2">{title}</h2>;
    $[2] = title;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <CustomLink href={ROUTES.RECOMMEND_POLICY_LIST.pathname} className="group flex items-center gap-4 py-8"><span className="font-600 text-Gray-600 transition-colors duration-150 typo-caption2 group-hover:text-Gray-900">더보기</span><SvgChevronright className="size-10 text-Gray-600 transition-colors duration-150 group-hover:text-Gray-900" /></CustomLink>;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t2) {
    t4 = <div className="flex items-center gap-10">{t2}{t3}</div>;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== item.customPolicyList) {
    t5 = item.customPolicyList.map(_temp2);
    $[7] = item.customPolicyList;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t4 || $[10] !== t5) {
    t6 = <div className="flex flex-col gap-20">{t4}{t5}</div>;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function _temp2(policy) {
  const row = <div className="group/row flex items-center gap-12 px-12"><Avatar size={48} src={policy.publisherLogoUrl} alt={policy.publisherName} /><div className="flex flex-1 flex-col items-start gap-4 overflow-hidden">{policy.supportBenefit && <span className="max-w-full truncate rounded-xsmall bg-M-25 px-6 py-2 font-600 text-M-400 typo-caption2">{policy.supportBenefit}</span>}<span className="w-full truncate font-600 text-Gray-black transition-colors duration-150 typo-body4 group-hover/row:text-M-500">{policy.title}</span></div></div>;
  if (policy.contentIdx) {
    return <CustomLink key={policy.contentIdx} prefetch={true} href={ROUTES.POLICY_DETAIL.pathname({
      idIdx: policy.slugId ?? policy.contentIdx
    })}>{row}</CustomLink>;
  }
  return <div key={policy.contentId ?? policy.title}>{row}</div>;
}
type ContentPluginProps = {
  item: FeedItem;
  priority?: boolean;
  href?: string;
};
const CONTENT_PLUGINS: Partial<Record<NonNullable<FeedItem['feedType']>, ComponentType<ContentPluginProps>>> = {
  SINGLE_IMAGE: ImageContent,
  MULTI_IMAGE: ImageContent,
  PROGRESS: ProgressContent,
  PRODUCT: ProductContent,
  POLICY_GROUP: PolicyGroupContent,
  TOUR: TourContent
};
function FeedCardActions(t0) {
  const $ = _c(31);
  const {
    item,
    href
  } = t0;
  const commentCount = item.commentCount ?? 0;
  const router = useCustomRouter();
  const {
    bookmark
  } = useBookmark();
  const {
    triggerHaptic
  } = useHaptic();
  const isBookmarked = item.wishlistYN ?? false;
  const bookmarkType = item.feedContentType ? FEED_BOOKMARK_TYPE_MAP[item.feedContentType] : undefined;
  const bookmarkContentId = item.contentId;
  let t1;
  if ($[0] !== href || $[1] !== item.feedContentType || $[2] !== router) {
    t1 = () => {
      if (!href) {
        return;
      }
      router.push(item.feedContentType === "LOCAL_MARKET" ? `${href}?tab=comments` : `${href}#${SELECTOR.COMMENT_LIST_TOP}`);
    };
    $[0] = href;
    $[1] = item.feedContentType;
    $[2] = router;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const handleComment = t1;
  let t2;
  if ($[4] !== href || $[5] !== triggerHaptic) {
    t2 = () => {
      if (!href) {
        return;
      }
      triggerHaptic();
      openShareDrawer(`${ENV.NEXT_PUBLIC_DOMAIN}${href}`);
    };
    $[4] = href;
    $[5] = triggerHaptic;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleShare = t2;
  let t3;
  if ($[7] !== bookmark || $[8] !== bookmarkContentId || $[9] !== bookmarkType || $[10] !== isBookmarked || $[11] !== item.title) {
    t3 = () => {
      if (!bookmarkType || !bookmarkContentId) {
        return;
      }
      bookmark({
        contentId: bookmarkContentId,
        isBookmarked,
        bookmarkType,
        contentName: item.title
      });
    };
    $[7] = bookmark;
    $[8] = bookmarkContentId;
    $[9] = bookmarkType;
    $[10] = isBookmarked;
    $[11] = item.title;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleBookmark = t3;
  let t4;
  if ($[13] !== bookmarkType || $[14] !== commentCount || $[15] !== handleComment) {
    t4 = bookmarkType && <button className="group flex items-center gap-2" aria-label={"\uB313\uAE00"} onClick={handleComment}><SvgChatbubbleTypingFill className="size-20 text-Gray-400 transition-colors duration-150 group-hover:text-Gray-600" />{commentCount > 0 && <span className="font-600 text-Gray-600 typo-caption1">{commentCount.toLocaleString("ko-KR")}</span>}</button>;
    $[13] = bookmarkType;
    $[14] = commentCount;
    $[15] = handleComment;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== handleShare || $[18] !== href) {
    t5 = href && <button className="group" aria-label={"\uACF5\uC720"} onClick={handleShare}><SvgSend className="size-20 text-Gray-400 transition-colors duration-150 group-hover:text-Gray-600" /></button>;
    $[17] = handleShare;
    $[18] = href;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== t4 || $[21] !== t5) {
    t6 = <div className="flex items-center gap-12">{t4}{t5}</div>;
    $[20] = t4;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== bookmarkContentId || $[24] !== bookmarkType || $[25] !== handleBookmark || $[26] !== isBookmarked) {
    t7 = bookmarkType && bookmarkContentId && <button className="group" aria-label={isBookmarked ? "\uBD81\uB9C8\uD06C \uD574\uC81C" : "\uBD81\uB9C8\uD06C"} onClick={handleBookmark}><SvgBookmarkFill className={cn("size-20 text-Gray-400 transition-colors duration-150", {
        "text-B-500": isBookmarked,
        "group-hover:text-Gray-600": !isBookmarked
      })} /></button>;
    $[23] = bookmarkContentId;
    $[24] = bookmarkType;
    $[25] = handleBookmark;
    $[26] = isBookmarked;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== t6 || $[29] !== t7) {
    t8 = <div className="flex items-center justify-between">{t6}{t7}</div>;
    $[28] = t6;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  return t8;
}
function FeedCardComment(t0) {
  const $ = _c(25);
  const {
    item,
    href
  } = t0;
  const comment = item.representComment;
  if (!comment?.comment) {
    return null;
  }
  const t1 = comment.nickname ?? "\uC775\uBA85";
  let t2;
  if ($[0] !== comment.profileImage || $[1] !== t1) {
    t2 = <Avatar size={24} src={comment.profileImage} alt={t1} />;
    $[0] = comment.profileImage;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const t3 = comment.nickname ?? "\uC775\uBA85";
  let t4;
  if ($[3] !== t3) {
    t4 = <span className="font-600 text-Gray-900 typo-caption2">{t3}</span>;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t2 || $[6] !== t4) {
    t5 = <div className="flex items-center gap-4">{t2}{t4}</div>;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== comment.createdAt) {
    t6 = formatDate(comment.createdAt);
    $[8] = comment.createdAt;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t6) {
    t7 = <span className="font-400 text-Gray-550 typo-caption2">{t6}</span>;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== t5 || $[13] !== t7) {
    t8 = <div className="flex items-center gap-8">{t5}{t7}</div>;
    $[12] = t5;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== comment.comment) {
    t9 = <p className="line-clamp-1 pl-28 font-400 text-Gray-600 typo-caption1">{comment.comment}</p>;
    $[15] = comment.comment;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t8 || $[18] !== t9) {
    t10 = <>{t8}{t9}</>;
    $[17] = t8;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  const content = t10;
  if (href) {
    const t11 = `${href}#${SELECTOR.COMMENT_LIST_TOP}`;
    let t12;
    if ($[20] !== content || $[21] !== t11) {
      t12 = <CustomLink prefetch={true} href={t11}>{content}</CustomLink>;
      $[20] = content;
      $[21] = t11;
      $[22] = t12;
    } else {
      t12 = $[22];
    }
    return t12;
  }
  let t11;
  if ($[23] !== content) {
    t11 = <div>{content}</div>;
    $[23] = content;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  return t11;
}
export const FeedCard = memo(function FeedCard(t0) {
  const $ = _c(27);
  const {
    item,
    priority
  } = t0;
  const ContentPlugin = item.feedType ? CONTENT_PLUGINS[item.feedType] : undefined;
  let t1;
  if ($[0] !== item) {
    t1 = getFeedDetailHref(item);
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const href = t1;
  const isPolicyGroup = item.feedType === "POLICY_GROUP";
  const isProduct = item.feedType === "PRODUCT";
  const isTour = item.feedType === "TOUR";
  const isOutsideLink = isProduct || isTour || item.feedType === "MULTI_IMAGE";
  let t2;
  if ($[2] !== ContentPlugin || $[3] !== isOutsideLink || $[4] !== isPolicyGroup || $[5] !== item || $[6] !== priority) {
    t2 = isPolicyGroup ? ContentPlugin && <ContentPlugin item={item} priority={priority} /> : <><FeedCardHeader item={item} /><FeedCardBody item={item} />{!isOutsideLink && ContentPlugin && <ContentPlugin item={item} priority={priority} />}</>;
    $[2] = ContentPlugin;
    $[3] = isOutsideLink;
    $[4] = isPolicyGroup;
    $[5] = item;
    $[6] = priority;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const linkedContent = t2;
  let t3;
  if ($[8] !== href || $[9] !== linkedContent) {
    t3 = href ? <CustomLink prefetch={true} href={href} className="group flex flex-col gap-8">{linkedContent}</CustomLink> : <div className="flex flex-col gap-8">{linkedContent}</div>;
    $[8] = href;
    $[9] = linkedContent;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== ContentPlugin || $[12] !== href || $[13] !== isOutsideLink || $[14] !== item || $[15] !== priority) {
    t4 = isOutsideLink && ContentPlugin && <ContentPlugin href={href} item={item} priority={priority} />;
    $[11] = ContentPlugin;
    $[12] = href;
    $[13] = isOutsideLink;
    $[14] = item;
    $[15] = priority;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[17] !== href || $[18] !== isPolicyGroup || $[19] !== isProduct || $[20] !== isTour || $[21] !== item) {
    t5 = !isProduct && !isTour && !isPolicyGroup && <><FeedCardActions item={item} href={href} /><FeedCardComment item={item} href={href} /></>;
    $[17] = href;
    $[18] = isPolicyGroup;
    $[19] = isProduct;
    $[20] = isTour;
    $[21] = item;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  let t6;
  if ($[23] !== t3 || $[24] !== t4 || $[25] !== t5) {
    t6 = <article className="bg-Gray-white px-16 py-24 xl:px-24"><div className="flex flex-col gap-20">{t3}{t4}{t5}</div></article>;
    $[23] = t3;
    $[24] = t4;
    $[25] = t5;
    $[26] = t6;
  } else {
    t6 = $[26];
  }
  return t6;
});